<template>
  <screen
    class="footscan-container"
    :can-skip="false"
    :can-leave="ready"
    :screen-info="info"
    :error="error && ready"
    @leave="leave"
  >
    <div v-show="showStandUp">
      <img v-show="showStandUp === 1" src="../../assets/FootScan/standup-sit.svg" :alt="info" />
      <img v-show="showStandUp === 2" src="../../assets/FootScan/standup-up.svg" :alt="info" />
    </div>
    <div v-show="!showStandUp" class="foot-container tall:mt-20" :class="{ positioning }">
      <img v-show="positioning" src="../../assets/FootScan/footscan-feet.svg" :alt="info" />
      <img v-show="stepTwo && ready" src="../../assets/FootScan/stand-up.svg" :alt="info" />
      <img v-show="state > 0" src="../../assets/FootScan/feet-opaque.svg" :alt="info" />
      <countdown v-if="countdownTimer > 0" :time="countdownTimer" />
      <scan-progress v-show="scanInProgress && !scanCompleted" :progress="scanProgress" />
      <scan-complete v-show="scanCompleted" />
    </div>
    <c-button
      class="mt-10 tall:mt-20 mx-auto"
      :class="{ invisible: !ready }"
      @click="startScanProcess"
    >
      <span>{{ $t('footscan.start_scan') }}</span>
    </c-button>
  </screen>
</template>

<script>
import audioPlayer from '@/mixins/audio-player';
import autoClearTimer from '@/mixins/auto-clear-timer';
import leave from '@/mixins/leave';

import ScanProcess from '@/modules/scan-process';
import { noop, wait } from '@/modules/utils';
import ScanComplete from '@/components/ScanBody/ScanComplete.vue';
import ScanProgress from '@/components/FootScan/ScanProgress.vue';
import Screen from '@/components/common/Screen.vue';
import Countdown from '@/components/common/Countdown.vue';
import CButton from '@/components/common/CButton.vue';

export default {
  name: 'Scan',
  components: {
    ScanComplete,
    ScanProgress,
    Screen,
    Countdown,
    CButton,
  },
  mixins: [audioPlayer, autoClearTimer, leave],
  mounted() {
    this.start();
  },
  data: () => ({
    state: -1,
    countdownTimer: 0,
    info: '',
    scanProgress: -1,
    scanCompleted: false,
    showIntro: true,
    error: false,
    stepTwo: false,
    showStandUp: 0, // 0: hidden, 1: sit, 2: stand up
  }),
  methods: {
    async countdown(timer) {
      this.countdownTimer = timer;
      await wait(1.3);
      this.countdownTimer -= 1;
      if (this.countdownTimer) {
        await this.countdown(this.countdownTimer);
      }
    },
    startProgress(to, quicker) {
      if (this.scanProgress >= to) return;

      const timeout = quicker ? 30 : 50;
      const increase = () => {
        if (this.scanProgress < to && !this.scanCompleted) {
          this.scanProgress += 1;
          setTimeout(increase, timeout);
        }
      };
      increase();
    },
    resetProgress() {
      this.scanProgress = -1;
    },
    setScanComplete() {
      this.scanCompleted = true;
      this.info = this.$t('scan.scan_complete');
    },
    async startScan(force) {
      if (this.state && !force) return;

      this.state = 1;
      await Promise.all([
        ScanProcess.start(this.stepTwo).then(noop),
        this.countdown(3).then(() => this.startProgress(99, false)),
      ]);
      await ScanProcess.waitScan(this.stepTwo);
      console.log('wait scan finished');
      this.startProgress(100, true);
      await this.waitForComplete();
      this.setScanComplete();
      this.resetProgress();
    },
    async waitForComplete() {
      await wait(0.5);
    },
    showError(message) {
      this.error = true;
      if (message) {
        this.info = message;
      }
    },
    finish() {
      if (!this.stepTwo) {
        this.stepTwo = true;
      } else {
        this.$events.goto('FootScan/Analyzing');
      }
    },
    reset() {
      this.state = -1;
      this.countdownTimer = 0;
      this.scanProgress = -1;
      this.scanCompleted = false;
      this.showStandUp = 0;
      this.error = false;
    },
    start() {
      this.info = this.$t('footscan.position_feet');
      this.playAndWait('foot_position_feet', 2, true)
        .then(() => {
          this.state = 0;
          if (!this.stepTwo) return Promise.resolve();

          this.info = this.$t('footscan.stand_still');
          return this.playAndWait('foot_face_forward', 1);
        })
        .then(() => {
          if (this.state === 0) {
            this.playAudio('foot_click_start');
          }
        });
    },
    async startScanProcess() {
      if (!this.ready) return;

      try {
        this.playAudio('foot_scan_in_progress', true);
        await this.startScan(true);
        await this.playAudio('14-Scan_complete', true);
        await wait(1);
        this.finish();
      } catch (e) {
        console.log(`[ERROR] scan failed! ${e.message}`);
        this.$events.alert(this.$t('welcome.unexpected_error'));
        this.leave(this.$t('welcome.unexpected_error'));
      }
    },
  },
  computed: {
    scanInProgress() {
      return this.scanProgress > -1;
    },
    screenName() {
      if (!this.showIntro) return '';

      if (this.stepTwo) return this.$t('scan.begin_2nd');

      return this.$t('scan.step_name');
    },
    ready() {
      return this.state === 0;
    },
    positioning() {
      return this.stepTwo ? this.state === -1 : this.state < 1;
    },
  },
  watch: {
    stepTwo(nv) {
      if (nv) {
        this.reset();
        this.showStandUp = 1;
        this.playAndWait('foot_standup', 3).then(() => {
          this.showStandUp = 0;
          this.start();
        });
        wait(1.5).then(() => {
          this.showStandUp = 2;
        });
      }
    },
    state(nv) {
      if (nv === 1) {
        this.info = this.$t('footscan.ready_to_scan');
      }
    },
    countdownTimer(nv, ov) {
      if (nv === 0 && ov === 1) {
        this.info = this.$t('scan.scan_in_progress');
      }
    },
    showStandUp(nv) {
      if (nv) {
        this.info = this.$t('footscan.stand_up');
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.footscan-container {
  ::v-deep {
    .content-container {
      @apply py-0;
    }
  }
}
.foot-container {
  @apply relative flex w-full justify-center items-center;
  @screen sm {
    max-width: 50%;
  }
  &.positioning {
    @apply p-10;
    &:before {
      @apply absolute inline-block top-0 left-0 w-full h-full;
      content: '';
      border: 1px solid black;
      border-radius: 10px;
    }
  }
}
</style>
